import { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";

import DoneIcon from "@mui/icons-material/Done";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import DeleteIcon from "@mui/icons-material/Delete";

import api from "../../utils/api";
import { useAuth } from "../../context/AuthContext";

export default function AdminContactInbox() {
  const [messages, setMessages] = useState([]);
  const [openNoteDialog, setOpenNoteDialog] = useState(false);
  const [currentMessageId, setCurrentMessageId] = useState(null);
  const [adminNote, setAdminNote] = useState("");
  const [toast, setToast] = useState({ open: false, msg: "", severity: "info" });

  const { user } = useAuth();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  /* ===================== LOAD ===================== */

  const loadMessages = async () => {
    try {
      const res = await api.get("/admin/contacts");
      setMessages(res.data.messages);
    } catch (error) {
      console.error("Failed to fetch messages:", error);
      setToast({
        open: true,
        msg: "Failed to load messages",
        severity: "error",
      });
    }
  };

  useEffect(() => {
    loadMessages();
  }, []);

  /* ===================== ACTIONS ===================== */

  const saveNote = async () => {
    try {
      await api.patch(`/admin/contacts/${currentMessageId}/note`, {
        admin_note: adminNote,
      });
      setToast({
        open: true,
        msg: "Note saved successfully",
        severity: "success",
      });
      loadMessages();
      setOpenNoteDialog(false);
    } catch (error) {
      console.error("Failed to save note:", error);
      setToast({
        open: true,
        msg: "Failed to save note",
        severity: "error",
      });
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/contacts/${id}/status`, { status });
      loadMessages();
      setToast({
        open: true,
        msg: `Message marked as ${status}`,
        severity: "success",
      });
    } catch (error) {
      console.error(`Failed to mark message as ${status}:`, error);
      setToast({
        open: true,
        msg: "Failed to update status",
        severity: "error",
      });
    }
  };

  const deleteMessage = async (id) => {
    try {
      await api.delete(`/admin/contacts/${id}`);
      loadMessages();
      setToast({
        open: true,
        msg: "Message deleted",
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to delete message:", error);
      setToast({
        open: true,
        msg: "Failed to delete message",
        severity: "error",
      });
    }
  };

  /* ===================== UI ===================== */

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Typography
        variant={isMobile ? "h6" : "h5"}
        fontWeight={800}
        gutterBottom
      >
        Contact Messages
      </Typography>

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 3,
          p: 2,
          overflowX: "auto", // 🔑 mobile safety
        }}
      >
        <Table size="small" sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {messages.map((message) => (
              <TableRow key={message.id} hover>
                <TableCell>{message.name}</TableCell>

                <TableCell>
                  <a href={`mailto:${message.email}`}>
                    {message.email}
                  </a>
                </TableCell>

                <TableCell
                  sx={{
                    maxWidth: { xs: 180, sm: 320 },
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {message.message}
                </TableCell>

                <TableCell>
                  <Chip
                    label={message.status}
                    size="small"
                    color={
                      message.status === "unread"
                        ? "warning"
                        : message.status === "replied"
                        ? "success"
                        : "info"
                    }
                  />
                </TableCell>

                <TableCell align="right">
                  <Stack
                    direction="row"
                    spacing={0.5}
                    justifyContent="flex-end"
                    flexWrap="wrap"
                  >
                    {message.status === "unread" && (
                      <Tooltip title="Mark as read">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() =>
                            updateStatus(message.id, "read")
                          }
                        >
                          <MarkEmailReadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}

                    {message.status !== "replied" && (
                      <Tooltip title="Mark as replied">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() =>
                            updateStatus(message.id, "replied")
                          }
                        >
                          <DoneIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}

                    <Tooltip title="Add note">
                      <IconButton
                        size="small"
                        color="info"
                        onClick={() => {
                          setAdminNote(message.admin_note || "");
                          setCurrentMessageId(message.id);
                          setOpenNoteDialog(true);
                        }}
                      >
                        <DoneIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Delete message">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() =>
                          deleteMessage(message.id)
                        }
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* NOTE DIALOG */}
      <Dialog
        open={openNoteDialog}
        onClose={() => setOpenNoteDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add Admin Note</DialogTitle>
        <DialogContent>
          <TextField
            label="Admin Note"
            fullWidth
            multiline
            rows={4}
            value={adminNote}
            onChange={(e) => setAdminNote(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNoteDialog(false)}>
            Cancel
          </Button>
          <Button onClick={saveNote} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* TOAST */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() =>
          setToast((prev) => ({ ...prev, open: false }))
        }
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert variant="filled" severity={toast.severity}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
